// src/components/card/CardText.jsx
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import remarkGfm from "remark-gfm";

const ReactMarkdown = dynamic(() => import("react-markdown").then((mod) => mod.default), {
  ssr: false,
  loading: () => <div className="text-sm text-[var(--muted)]">Loading…</div>,
});


export default function CardText({
  content = "",
  variant = "markdown",
  clampLines = 0,
  expandable = true,
  initiallyExpanded = false,
  expandOnFocus = false,
  markdownOptions,
  className = "text-[15px] leading-6",
  hideToggle = false,
  onToggleStateChange,
}) {
  const [expanded, setExpanded] = useState(Boolean(initiallyExpanded));
  const [showToggle, setShowToggle] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (expandOnFocus) setExpanded(true);
  }, [expandOnFocus]);

  // Markdown link override: make links match other clickable UI (brand color, no dotted underline)
  const mdLink = useMemo(
    () => ({
      h1: (props) => (
        <h1
          {...props}
          className={(props.className ? `${props.className} ` : "") + "mt-3 mb-1.5 text-xl font-semibold leading-tight text-[var(--fg-primary)] sm:text-2xl"}
        />
      ),
      h2: (props) => (
        <h2
          {...props}
          className={(props.className ? `${props.className} ` : "") + "mt-3 mb-1.5 text-lg font-semibold leading-tight text-[var(--fg-primary)] sm:text-xl"}
        />
      ),
      h3: (props) => (
        <h3
          {...props}
          className={(props.className ? `${props.className} ` : "") + "mt-2.5 mb-1 text-base font-semibold leading-snug text-[var(--fg-primary)] sm:text-lg"}
        />
      ),
      h4: (props) => (
        <h4
          {...props}
          className={(props.className ? `${props.className} ` : "") + "mt-2.5 mb-1 text-[15px] font-semibold leading-snug text-[var(--fg-primary)] sm:text-base"}
        />
      ),
      p: (props) => (
        <p
          {...props}
          className={(props.className ? `${props.className} ` : "") + "my-2"}
        />
      ),
      a: (props) => {
        const href = props.href || "";
        const external = /^https?:\/\//i.test(href);
        return (
          <a
            {...props}
            className={
              (props.className ? props.className + " " : "") +
              "relative z-20 pointer-events-auto text-[var(--brand)] no-underline hover:text-[var(--brand)]"
            }
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer nofollow" : undefined}
            onClick={(e) => {
              e.stopPropagation();
              if (!external && href === "#") {
                e.preventDefault();
              }
            }}
          />
        );
      },
    }),
    []
  );

  

  useEffect(() => {
    onToggleStateChange?.({
      expanded,
      showToggle,
      toggle: () => setExpanded((v) => !v),
    });
  }, [expanded, onToggleStateChange, showToggle]);

  useEffect(() => {
    return () => {
      onToggleStateChange?.({
        expanded: false,
        showToggle: false,
        toggle: null,
      });
    };
  }, [onToggleStateChange]);

  useEffect(() => {
    if (!expandable || clampLines <= 0) {
      setShowToggle(false);
      return;
    }

    const el = textRef.current;
    if (!el) return;

    const measure = () => {
      const prev = el.style.cssText;

      el.style.display = "-webkit-box";
      el.style.webkitBoxOrient = "vertical";
      el.style.webkitLineClamp = String(clampLines);
      el.style.overflow = "hidden";

      const overflowed = el.scrollHeight > el.clientHeight + 1;
      setShowToggle(overflowed);

      el.style.cssText = prev;
    };

    measure();

    let ro;
    if ("ResizeObserver" in window) {
      ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    } else {
      const onResize = () => measure();
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }
  }, [content, clampLines, expandable]);

  const collapsedClampStyle =
    !expanded && clampLines > 0
      ? {
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: String(clampLines),
          overflow: "hidden",
        }
      : undefined;

  return (
    <div className="relative">
      <div ref={textRef} className={className} style={collapsedClampStyle}>
        {variant === "markdown" ? (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{ ...mdLink, ...(markdownOptions?.components || {}) }}
            {...markdownOptions}
          >
            {content}
          </ReactMarkdown>
        ) : (
          <p>{content}</p>
        )}
      </div>

      {expandable && showToggle && !hideToggle ? (
        <div
          className={
            expanded
              ? "mt-1 flex justify-end"
              : "absolute bottom-0 right-0 flex h-6 items-center pl-5"
          }
          style={
            expanded
              ? undefined
              : {
                  background:
                    "linear-gradient(to right, transparent, var(--card) 1.25rem)",
                }
          }
        >
          <button
            type="button"
            className="relative z-20 pointer-events-auto bg-[var(--card)] text-sm font-medium text-[var(--brand)] hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
            aria-expanded={expanded}
          >
            {expanded ? "Less" : "… More"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
